import * as React from "react";
import PlayerIdentityRow from "../profile/PlayerIdentityRow";
import { useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import { useDataProvider, useNotify } from "react-admin";
import { useQuery } from "@tanstack/react-query";
import { useClub } from "./ClubContext";

function ruleLabel(rule: string) {
  switch (rule) {
    case "trainings_completed":
      return "Trainings abgeschlossen";
    case "numeric_games_completed":
      return "Numeric Trainings";
    default:
      return rule;
  }
}

function fmtDate(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
}

function fmtDateTime(d?: string | null) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
}

export default function ClubLeaderboardPage() {
  const { clubId } = useClub();
  const dp = useDataProvider();
  const notify = useNotify();

  const canLoad = useMemo(() => !!clubId, [clubId]);

  // Leaderboard Query (wird durch react-admin Refresh automatisch neu gefetched)
  const leaderboardQuery = useQuery({
    queryKey: ["leaderboard", "club", clubId],
    enabled: !!clubId,
    staleTime: 0,
    queryFn: async () => {
      const lb = await dp.custom(`leaderboard/club?club_id=${clubId}&limit=50`, {
        method: "GET",
      });
      return lb.data ?? [];
    },
  });

  // Challenges Query (wird durch react-admin Refresh automatisch neu gefetched)
  const challengesQuery = useQuery({
    queryKey: ["leaderboard", "club", "challenges", clubId],
    enabled: !!clubId,
    staleTime: 0,
    queryFn: async () => {
      const ch = await dp.custom(
        `leaderboard/club/challenges?club_id=${clubId}&top_n=5`,
        { method: "GET" }
      );
      return ch.data ?? [];
    },
  });

  // Fehler einmalig notifizieren (statt in queryFn try/catch -> verhindert “Doppel-Notify”)
  useEffect(() => {
    if (leaderboardQuery.isError || challengesQuery.isError) {
      notify("Konnte Rangliste/Challenges nicht laden", { type: "warning" });
    }
  }, [leaderboardQuery.isError, challengesQuery.isError, notify]);

  const board = (leaderboardQuery.data ?? []) as any[];
  const challenges = (challengesQuery.data ?? []) as any[];
  const loading = leaderboardQuery.isLoading || challengesQuery.isLoading;

  if (!canLoad) {
    return <Alert severity="info">Bitte zuerst einen Verein auswählen.</Alert>;
  }

  return (
    <Stack spacing={2} sx={{ p: 2, width: "100%" }}>
      <Typography variant="h5" fontWeight={700}>
        Rangliste & Challenges
      </Typography>

      {loading ? (
        <Alert severity="info">Lade Daten…</Alert>
      ) : null}

      <Box sx={{ display: "grid", gap: 2 }}>
        {/* Gesamt Rangliste */}
        <Card>
          <CardContent>
            <Typography variant="h6">Gesamt-Rangliste</Typography>
            <Typography variant="body2" color="text.secondary"></Typography>

            <Divider sx={{ my: 2 }} />

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Spieler</TableCell>
                  <TableCell align="right">Punkte</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {board.map((r, idx) => (
                  <TableRow key={r.user_id}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700 }}>{idx + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <PlayerIdentityRow
                        firstName={r.first_name}
                        lastName={r.last_name}
                        email=""
                        points={r.points}
                        avatarUrl={r.avatar_url ?? r.avatar}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 700 }}>{r.points}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {!board.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography color="text.secondary">Noch keine Daten.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Challenges + Ranglisten */}
        <Card>
          <CardContent>
            <Typography variant="h6">Challenges </Typography>
            <Typography variant="body2" color="text.secondary">
              Zeigt aktive & inaktive Challenges sowie die ersten Abschlüsse.
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={2}>
              {challenges.map((c) => (
                <Card key={c.id} variant="outlined">
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={2}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 800 }}>{c.name}</Typography>
                        {c.description ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {c.description}
                          </Typography>
                        ) : null}

                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          sx={{ mt: 1 }}
                        >
                          <Chip
                            size="small"
                            label={ruleLabel(c.rule_type)}
                            variant="outlined"
                          />
                          <Chip size="small" label={`Ziel: ${c.target}`} variant="outlined" />
                          {c.window_days ? (
                            <Chip size="small" label={`${c.window_days} Tage`} variant="outlined" />
                          ) : null}
                          <Chip size="small" label={`Reward: ${c.reward_points}`} />
                          <Chip
                            size="small"
                            color={c.is_active ? "success" : "default"}
                            label={c.is_active ? "aktiv" : "inaktiv"}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`von ${fmtDate(c.starts_on)} bis ${fmtDate(c.ends_on)}`}
                          />
                        </Stack>
                      </Box>

                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${c.completions_count} Abschlüsse`}
                      />
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="subtitle2">
                      Challenge-Rangliste (Top {c.top_completions?.length ?? 0})
                    </Typography>

                    <Table size="small" sx={{ mt: 1 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Spieler</TableCell>
                          <TableCell>Abgeschlossen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(c.top_completions ?? []).map((x: any, idx: number) => (
                          <TableRow key={`${c.id}-${x.user_id}`}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{x.display_name ?? `User ${x.user_id}`}</TableCell>
                            <TableCell>{fmtDateTime(x.completed_at)}</TableCell>
                          </TableRow>
                        ))}
                        {(!c.top_completions || c.top_completions.length === 0) && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography color="text.secondary">
                                Noch keine Abschlüsse.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))}

              {!challenges.length && !loading && (
                <Typography color="text.secondary">Keine Challenges vorhanden.</Typography>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
